import { useCallback } from 'react';
import { useConfirm } from '../ui/ConfirmProvider';
import { reservationsApi, reservationsSaisieApi, type Reservation, type StatutReservation } from '../../lib/reservationsApi';
import { useCodeManager } from './useCodeManager';

/**
 * Actions sur une réservation communes au tableau et à la fiche : changer de statut (avec le contrôle de disponibilité à la
 * sortie de la liste d'attente), marquer absent, annuler. Chaque action demande le code manager si la direction l'a protégée.
 * Elles lèvent l'erreur du serveur : l'appelant l'affiche dans son propre message. Rendent `true` si l'action a été faite.
 */
export const useActionsReservation = () => {
    const confirmer = useConfirm();
    const { executer, fenetre } = useCodeManager();

    const changerStatut = useCallback(async (r: Reservation, vers: StatutReservation): Promise<boolean> => {
        if (r.statut === 'attente' && vers !== 'no_show') {
            const d = await reservationsSaisieApi.disponibilite(r.date, r.heure, r.couverts);
            if (!d.disponible) {
                if (!(await confirmer({ title: `Confirmer ${r.nom} malgré tout ?`, description: `Aucune place à ce créneau : ${d.raisons.join(' · ')}.`, confirmLabel: 'Confirmer malgré tout', destructive: false }))) return false;
                return !!(await executer((code) => reservationsApi.statut(r.id, vers, true, code)));
            }
        }
        return !!(await executer((code) => reservationsApi.statut(r.id, vers, false, code)));
    }, [confirmer, executer]);

    const marquerAbsent = useCallback(async (r: Reservation): Promise<boolean> => {
        if (!(await confirmer({ title: `Marquer ${r.nom} absent ?`, description: "La table est libérée tout de suite. Aucune demande d'avis ne sera envoyée à ce client. Vous pourrez annuler cette action.", confirmLabel: 'Marquer absent', destructive: false }))) return false;
        return !!(await executer((code) => reservationsApi.statut(r.id, 'no_show', false, code)));
    }, [confirmer, executer]);

    const annuler = useCallback(async (r: Reservation): Promise<boolean> => {
        if (!(await confirmer({ title: `Annuler la réservation de ${r.nom} ?`, description: 'La table est libérée tout de suite. La réservation reste dans le livre, en « Annulée », et vous pourrez annuler cette annulation.', confirmLabel: 'Annuler la réservation', destructive: false }))) return false;
        return !!(await executer((code) => reservationsApi.annuler(r.id, '', code)));
    }, [confirmer, executer]);

    return { changerStatut, marquerAbsent, annuler, executer, fenetre };
};
